<template>
    <section class="emailDraft" aria-label="Email draft" data-testid="email-draft">
        <BaseTabs v-model="activeTab" :tab-ids="tabIds" hide-single-tab-list list-class="emailDraftTabs">
            <template v-for="(variant, index) in variants" :key="`trigger-${index}`" #[`trigger-${index}`]>
                {{ variant.label || `Draft ${index + 1}` }}
            </template>
            <template v-for="(variant, index) in variants" :key="`content-${index}`" #[`content-${index}`]>
                <p v-if="variant.subject" class="emailDraftSubject">
                    <span class="details text-valence-grey-750">Subject</span>
                    <span class="emailDraftSubjectValue nadia-chat">{{ variant.subject }}</span>
                </p>
                <p class="emailDraftBody nadia-chat">{{ variant.body }}</p>
                <div class="emailDraftActions">
                    <a
                        v-if="canOpenInMail(variant)"
                        :href="mailtoUrl(variant)"
                        class="base-btn-secondary base-btn-sm emailDraftAction"
                        data-testid="email-draft-open-in-mail"
                        @click="logOpenInMail(index)">
                        <i class="bi bi-envelope" aria-hidden="true"></i>
                        Open in mail
                    </a>
                    <BaseButton theme="primary" size="sm" class="emailDraftAction" data-testid="email-draft-copy" @click="copyEmail(variant, index)">
                        <Copy class="h-3.5 w-3.5" aria-hidden="true" />
                        {{ isCopied && copiedIndex === index ? "Copied!" : "Copy email" }}
                    </BaseButton>
                </div>
            </template>
        </BaseTabs>
        <p class="sr-only" role="status" data-testid="email-draft-copy-status">{{ copyStatus }}</p>
    </section>
</template>

<script setup lang="ts">
import BaseButton from "~vue/components/BaseButton.vue";
import BaseTabs from "~vue/components/BaseTabs.vue";
import { useTimedShow } from "~vue/composables/useTimedShow";
import Copy from "~vue/icons/Copy.vue";
import type { EmailDraftVariant, ParseActionsEmailDraftAction } from "~vue/types/chat";
import { logUserInteraction } from "~vue/utils/logUtils";
import { computed, inject, ref } from "vue";

type ChatEmailDraftProps = {
    data: ParseActionsEmailDraftAction;
    messageId?: number;
    lineIdx: number;
};

const { data, messageId, lineIdx } = defineProps<ChatEmailDraftProps>();

const coachingSessionUuid = inject<string>("coachingSessionUuid");
const { show: showCopied, isShowing: isCopied } = useTimedShow({ duration: 2000 });

const variants = computed(() => data.action_params.variants);
const tabIds = computed(() => variants.value.map((_, index) => `${index}`));
const activeTab = ref("0");

const emailText = (variant: EmailDraftVariant) => (variant.subject ? `Subject: ${variant.subject}\n\n${variant.body}` : variant.body);

const mailtoUrl = (variant: EmailDraftVariant) => `mailto:?subject=${encodeURIComponent(variant.subject)}&body=${encodeURIComponent(variant.body)}`;

// Mail clients and browsers cap mailto: around 2000 characters and truncate silently past it, so a
// long draft offers Copy email alone rather than a link that would drop the end of the body.
const canOpenInMail = (variant: EmailDraftVariant) => mailtoUrl(variant).length <= 2000;

const copiedIndex = ref<number | null>(null);
const copyStatus = computed(() => (isCopied.value ? "Email copied" : ""));

const copyEmail = async (variant: EmailDraftVariant, index: number) => {
    await navigator.clipboard.writeText(emailText(variant));
    copiedIndex.value = index;
    showCopied();
    logUserInteraction("email_draft_copied", { chat_message_id: messageId, line_idx: lineIdx, variant_index: index, variant_count: variants.value.length }, coachingSessionUuid);
};

const logOpenInMail = (index: number) => {
    logUserInteraction("email_draft_opened_in_mail", { chat_message_id: messageId, line_idx: lineIdx, variant_index: index }, coachingSessionUuid);
};
</script>

<style scoped>
@reference "../../../css/tailwind.css";

@layer components {
    .emailDraft {
        @apply my-4 w-full overflow-hidden rounded-lgr border border-valence-grey-250 bg-white;
    }

    .emailDraftSubject {
        @apply flex flex-col gap-1 border-b border-valence-grey-250 px-4 py-3 md:flex-row md:items-baseline md:gap-3;
    }

    /* min-w-0 lets the flex item shrink so a long unbroken subject wraps instead of overflowing the card. */
    .emailDraftSubjectValue {
        @apply min-w-0 break-words;
    }

    /* A long draft scrolls inside the card rather than pushing the rest of the conversation off screen. */
    .emailDraftBody {
        @apply max-h-96 overflow-y-auto overscroll-contain px-4 py-4 break-words whitespace-pre-line;
    }

    .emailDraftActions {
        @apply flex flex-wrap justify-end gap-2 px-4 pb-4;
    }

    .emailDraftAction {
        @apply inline-flex items-center gap-2;
    }

    :deep(.emailDraftTabs) {
        @apply px-2;
    }
}
</style>
